<template>
  <AuthLayout>
    <div class="logo-container">
      <img
        style="height: 100%; margin: 0 auto"
        :src="cdnBaseUrl + '/img/logos/retain/dots.svg'"
        alt=""
      />
    </div>
    <h1 class="text-3xl font-black mb-8 text-center mt-4 text-primary">Welcome Back</h1>

    <form @submit.prevent="submitLogin">
      <div class="flex flex-col">
        <InputGroup>
          <InputGroupAddon>
            <Icon type="envelope" />
          </InputGroupAddon>
          <Input
            v-model="formObject.email"
            required
            type="email"
            class="w-full"
            placeholder="Email"
            autocomplete="email"
            :has-error="v$.email.$error"
          />
        </InputGroup>
        <InputError :has-error="v$.email.$error">{{ v$.email.$errors[0]?.$message }}</InputError>
      </div>
      <div class="mt-3">
        <InputGroup>
          <InputGroupAddon>
            <Icon type="lock" />
          </InputGroupAddon>
          <Input
            v-model="formObject.password"
            required
            type="password"
            class="w-full"
            placeholder="Password"
            autocomplete="current-password"
            :has-error="v$.password.$error"
          />
        </InputGroup>
        <InputError :has-error="v$.password.$error">{{ v$.password.$errors[0]?.$message }}</InputError>
      </div>
      <div
        v-if="error.length > 0"
        class="text-danger bg-red-100 py-2 px-5 mt-5 rounded-md"
      >
        <Icon type="circle-exclamation" />
        {{ error }}
      </div>
      <Button
        label="Sign In"
        type="submit"
        block
        :is-loading="userStore.isAuthenticating"
        class="mt-5"
      />
    </form>

    <Button
      text
      label="Forgot Password?"
      router-link="/forgot-password"
      block
      class="mt-4"
    />
  </AuthLayout>
</template>

<script setup lang="ts">
// components
import Icon from '@/components/icon/Icon.vue'
import Button from '@/components/button/Button.vue'
import InputGroup from '@/components/forms/InputGroup.vue'
import InputGroupAddon from '@/components/forms/InputGroupAddon.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'

// views
import AuthLayout from '@/views/auth/AuthLayout.vue'

// stores
import { useUserStore } from '@/stores/UserStore'

// composables
import { ref, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import useVuelidate from '@vuelidate/core'
import { required, email, helpers } from '@vuelidate/validators'

/** Pinia user store instance */
const userStore = useUserStore()
/** Router instance */
const router = useRouter()
/** Global CDN base URL */
const cdnBaseUrl = (getCurrentInstance()?.appContext.config.globalProperties as Record<string, unknown>)
  .$cdnBaseUrl as string

/** Form data object */
const formObject = ref<{ email: string; password: string }>({
  email: import.meta.env.DEV ? import.meta.env.VITE_DEV_USER_EMAIL || '' : '',
  password: import.meta.env.DEV ? import.meta.env.VITE_DEV_USER_PASSWORD || '' : ''
})

/** Vuelidate rules */
const formRules = {
  email: {
    required: helpers.withMessage('Enter your Email', required),
    email: helpers.withMessage('Enter a valid email address', email)
  },
  password: {
    required: helpers.withMessage('Enter your Password', required)
  }
}

/** Vuelidate instance */
const v$ = useVuelidate(formRules, formObject)

/** Error message string for login failures */
const error = ref<string>('')

/**
 * Handle login form submission with redirect support.
 */
async function submitLogin(): Promise<void> {
  try {
    const isFormCorrect = await v$.value.$validate()
    if (!isFormCorrect) {
      return
    }

    const redirectRef = router.currentRoute.value.query.ref
    let redirectTo = '/'
    if (redirectRef && redirectRef != null) {
      redirectTo = redirectRef as string
    }

    const result = await userStore.login({ e: formObject.value.email, p: formObject.value.password }, redirectTo)
    if (!result.success) {
      error.value = result.message || 'Login failed. Please check your credentials.'
    }
  } catch (err) {
    error.value = 'Something went wrong. Please try again.'
    console.error('Login failed', err)
  }
}
</script>

<style scoped>
.logo-container {
  top: -20px;
  left: 0;
  right: 0;
  margin: 0 auto;
  margin-top: 0px;
  height: 80px;
  width: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: inherit;
  border-radius: var(--rounded-full);
  padding: var(--s-4);
  box-shadow: var(--shadow-md);
  z-index: 100;
}
</style>
